import React, { useContext, useEffect, useMemo, useState } from 'react';
import userPermissionContext from '../../../context/userPermissionContext';
import { KTSVG } from '../../../_metronic/helpers';

const API_URL = process.env.REACT_APP_API_URL || '';
const toNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const extractArray = (payload, key) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (key && Array.isArray(payload?.[key])) return payload[key];
	if (key && Array.isArray(payload?.data?.[key])) return payload.data[key];
	const first = Object.values(payload || {}).find(v => Array.isArray(v));
	if (first) return first;
	if (payload?.data && typeof payload.data === 'object') {
		const nested = Object.values(payload.data).find(v => Array.isArray(v));
		if (nested) return nested;
	}
	return [];
};

const RENEWAL_KEYS = [
	{ label: 'Tax Token', field: 'taxTokenExpirationCount' },
	{ label: 'Fitness', field: 'fitnessExpirationCount' },
	{ label: 'Route Permit', field: 'routePermitExpirationCount' },
	{ label: 'Insurance', field: 'insuranceExpirationCount' },
];

/* ── small UI helpers ── */
const CountBadge = ({ children, type }) => {
	const map = { danger: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }, warn: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' }, ok: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' } };
	const s = map[type] || map.ok;
	return <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{children}</span>;
};
const Bar = ({ value, max, color = '#94a3b8', height = 5 }) => (
	<div style={{ background: '#f1f5f9', borderRadius: height, height, width: '100%', overflow: 'hidden' }}>
		<div style={{ width: `${max > 0 ? Math.min((value / max) * 100, 100) : 0}%`, height: '100%', background: color, borderRadius: height, transition: 'width .5s ease' }} />
	</div>
);

function ManagementDashboard() {
	const { jwt, businessUnit } = useContext(userPermissionContext);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [fleet, setFleet] = useState({});
	const [expiryExpired, setExpiryExpired] = useState({});
	const [expirySoon, setExpirySoon] = useState({});
	const [headCost, setHeadCost] = useState([]);
	const [expenseBlock, setExpenseBlock] = useState({});
	const [expenseSummary, setExpenseSummary] = useState({});
	const [projectVehicle, setProjectVehicle] = useState([]);
	const [categoryVehicle, setCategoryVehicle] = useState([]);
	const [lastSync, setLastSync] = useState(null);

	useEffect(() => {
		let cancelled = false;
		const token = jwt?.token;
		if (!token) return;
		const headers = { Authorization: `Bearer ${token}` };
		const pid = businessUnit?.idProjects;
		const qp = pid != null && pid !== '' ? `?currentProject=${encodeURIComponent(pid)}&projectId=${encodeURIComponent(pid)}` : '';
		const get = (url) => fetch(url, { headers }).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

		(async () => {
			try {
				setLoading(true); setError('');
				const res = await Promise.allSettled([
					get(`${API_URL}vms_app_total_vehicle_count${qp}`),
					get(`${API_URL}vms_app_dashboard_expiration_count${qp}${qp ? '&' : '?'}documentAlertRange=expired`),
					get(`${API_URL}vms_app_dashboard_expiration_count${qp}${qp ? '&' : '?'}documentAlertRange=30`),
					get(`${API_URL}vms_app_dashboard_expense_head_total_cost${qp}`),
					get(`${API_URL}vms_app_expense_cost_summary${qp}`),
					get(`${API_URL}vms_app_project_wise_vehicle${qp}`),
					get(`${API_URL}vms_app_category_wise_vehicle_count${qp}`),
				]);
				if (cancelled) return;
				const p = i => res[i]?.status === 'fulfilled' ? res[i].value : null;
				setFleet(p(0)?.data || {});
				setExpiryExpired(p(1)?.data || {});
				setExpirySoon(p(2)?.data || {});
				const rHead = p(3);
				setExpenseBlock(rHead?.data || {});
				setHeadCost(extractArray(rHead, 'expenseHeadWiseTotalCost'));
				setExpenseSummary(p(4)?.data || {});
				setProjectVehicle(extractArray(p(5), 'projectWiseVehicle'));
				setCategoryVehicle(extractArray(p(6), 'categoryWiseVehicleCount'));
				setLastSync(new Date());
				const failed = res.filter(r => r.status === 'rejected').length;
				if (failed > 0) setError(`Partial data loaded (${failed} source${failed > 1 ? 's' : ''} unavailable).`);
			} catch { if (!cancelled) setError('Failed to load dashboard.'); }
			finally { if (!cancelled) setLoading(false); }
		})();
		return () => { cancelled = true; };
	}, [jwt?.token, businessUnit?.idProjects]);

	/* derived */
	const totalV = toNum(fleet.totalVehicleCount);
	const activeV = toNum(fleet.totalActiveVehicles);
	const inactiveV = toNum(fleet.totalInactiveVehicles);
	const inHouse = toNum(fleet.inHouseVehicleCount ?? fleet.inHouseVehcileCount);
	const thirdParty = toNum(fleet.thirdPartyVehicleCount);

	const expTotal = toNum(expenseBlock.totalCost ?? expenseSummary.totalCost);
	const expVariance = toNum(expenseBlock.totalCostVariancePercentage ?? expenseSummary.costVariancePercentage);
	const costPerVehicle = activeV > 0 ? Math.round(expTotal / activeV) : 0;

	const expiredTotal = useMemo(() => RENEWAL_KEYS.reduce((s, k) => s + toNum(expiryExpired?.[k.field]), 0), [expiryExpired]);
	const soonTotal = useMemo(() => RENEWAL_KEYS.reduce((s, k) => s + toNum(expirySoon?.[k.field]), 0), [expirySoon]);

	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;

	/* style tokens */
	const card = { borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', padding: '14px 18px', height: '100%' };
	const kpiLabel = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: .3, marginBottom: 4 };
	const kpiValue = { fontSize: 24, fontWeight: 700, color: '#1e293b', lineHeight: 1.15 };
	const kpiSub = { fontSize: 11, color: '#94a3b8', marginTop: 2 };
	const sectionHead = (text) => <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: '#64748b', marginBottom: 8, paddingBottom: 4, borderBottom: '2px solid #f1f5f9' }}>{text}</div>;
	const tblHeader = { background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569' };
	const panelHead = (text, href, hrefLabel) => (
		<div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
			<span style={{ fontWeight: 700, fontSize: 13, color: '#334155' }}>{text}</span>
			{href && <a href={`#${href}`} style={{ fontSize: 11, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>{hrefLabel || 'View →'}</a>}
		</div>
	);

	return (
		<div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
			{/* Header */}
			<div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<KTSVG path='/media/svg/icons/Layout/Layout-4-blocks.svg' className='svg-icon-1 svg-icon-primary' />
					<span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Fleet Management Dashboard</span>
					{businessUnit?.projectName && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>— {businessUnit.projectName}</span>}
				</div>
				<span style={{ fontSize: 11, color: '#94a3b8' }}>{lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : 'Loading...'}</span>
			</div>

			<div style={{ padding: '20px' }}>
				{error && <div className='alert alert-warning py-2 mb-3' style={{ fontSize: 12 }}>{error}</div>}

				{/* ── Fleet counts ── */}
				{sectionHead('Fleet Overview')}
				<div className='row g-3 mb-4'>
					<div className='col-6 col-md-4 col-xl'>
						<div style={card}>
							<div style={kpiLabel}>Total Vehicles</div>
							<div style={kpiValue}>{V(totalV)}</div>
						</div>
					</div>
					<div className='col-6 col-md-4 col-xl'>
						<div style={card}>
							<div style={kpiLabel}>Active</div>
							<div style={{ ...kpiValue, color: '#166534' }}>{V(activeV)}</div>
						</div>
					</div>
					<div className='col-6 col-md-4 col-xl'>
						<div style={card}>
							<div style={kpiLabel}>Inactive</div>
							<div style={{ ...kpiValue, color: '#64748b' }}>{V(inactiveV)}</div>
						</div>
					</div>
					<div className='col-6 col-md-4 col-xl'>
						<div style={card}>
							<div style={kpiLabel}>In-House (Active)</div>
							<div style={{ ...kpiValue, color: '#1e40af' }}>{V(inHouse)}</div>
						</div>
					</div>
					<div className='col-6 col-md-4 col-xl'>
						<div style={card}>
							<div style={kpiLabel}>Third-Party (Active)</div>
							<div style={{ ...kpiValue, color: '#6d28d9' }}>{V(thirdParty)}</div>
						</div>
					</div>
				</div>

				{/* ── Expense summary ── */}
				{sectionHead('Expense Summary')}
				<div className='row g-3 mb-4'>
					<div className='col-6 col-md-4'>
						<div style={card}>
							<div style={kpiLabel}>Total Expense (Period)</div>
							<div style={{ ...kpiValue, fontSize: 20 }}>{V(`৳${expTotal.toLocaleString()}`)}</div>
							{!loading && expVariance !== 0 && (
								<div style={{ ...kpiSub, color: expVariance > 0 ? '#b45309' : '#166534' }}>
									{expVariance > 0 ? '↑' : '↓'} {Math.abs(expVariance)}% vs prior period
								</div>
							)}
						</div>
					</div>
					<div className='col-6 col-md-4'>
						<div style={card}>
							<div style={kpiLabel}>Avg Cost / Active Vehicle</div>
							<div style={{ ...kpiValue, fontSize: 20 }}>{V(`৳${costPerVehicle.toLocaleString()}`)}</div>
							<div style={kpiSub}>Based on {activeV} active vehicles</div>
						</div>
					</div>
					<div className='col-6 col-md-4'>
						<div style={{ ...card, borderColor: expiredTotal > 0 ? '#fca5a5' : '#e2e8f0' }}>
							<div style={kpiLabel}>Document Alerts</div>
							<div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
								<div><span style={{ fontSize: 18, fontWeight: 700, color: expiredTotal > 0 ? '#dc2626' : '#1e293b' }}>{V(expiredTotal)}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>Expired</div></div>
								<div><span style={{ fontSize: 18, fontWeight: 700, color: soonTotal > 0 ? '#d97706' : '#1e293b' }}>{V(soonTotal)}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>Due 30d</div></div>
							</div>
						</div>
					</div>
				</div>

				{/* ── Renewal detail + Expense heads ── */}
				<div className='row g-3 mb-4'>
					<div className='col-lg-5'>
						<div style={{ borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', height: '100%' }}>
							{panelHead('Document Renewals', '/to-do-list', 'To-do list →')}
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead><tr style={tblHeader}>
									<th style={{ paddingLeft: 14 }}>Document</th><th className='text-center'>Expired</th><th className='text-center'>Due 30d</th><th className='text-center'>Total</th>
								</tr></thead>
								<tbody>
									{RENEWAL_KEYS.map(({ label, field }) => {
										const exp = toNum(expiryExpired?.[field]);
										const soon = toNum(expirySoon?.[field]);
										return (
											<tr key={field}>
												<td style={{ paddingLeft: 14 }}>{label}</td>
												<td className='text-center'>{exp > 0 ? <CountBadge type='danger'>{exp}</CountBadge> : '0'}</td>
												<td className='text-center'>{soon > 0 ? <CountBadge type='warn'>{soon}</CountBadge> : '0'}</td>
												<td className='text-center fw-bold'>{exp + soon}</td>
											</tr>
										);
									})}
									<tr style={{ background: '#f8fafc', fontWeight: 600 }}>
										<td style={{ paddingLeft: 14 }}>Total</td>
										<td className='text-center'>{expiredTotal > 0 ? <CountBadge type='danger'>{expiredTotal}</CountBadge> : '0'}</td>
										<td className='text-center'>{soonTotal > 0 ? <CountBadge type='warn'>{soonTotal}</CountBadge> : '0'}</td>
										<td className='text-center'>{expiredTotal + soonTotal}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div className='col-lg-7'>
						<div style={{ borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
							{panelHead('Expense Head Wise Cost', '/report-vehicleExpenseReport', 'Expense report →')}
							<div style={{ maxHeight: 260, overflowY: 'auto', flex: 1 }}>
								<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
									<thead><tr style={tblHeader}>
										<th style={{ paddingLeft: 14 }}>Head</th><th className='text-end'>Amount (৳)</th><th style={{ width: 100 }}>Share</th>
									</tr></thead>
									<tbody>
										{(headCost || []).map((r, i) => {
											const cost = toNum(r?.totalCost ?? r?.total ?? r?.amount ?? r?.cost ?? r?.expenseAmount);
											const share = expTotal > 0 ? (cost / expTotal) * 100 : 0;
											return (
												<tr key={`h-${i}`}>
													<td style={{ paddingLeft: 14 }}>{r?.head_name || r?.headName || '-'}</td>
													<td className='text-end fw-bold'>{cost.toLocaleString()}</td>
													<td><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
														<Bar value={share} max={100} color='#94a3b8' /><span style={{ fontSize: 10, color: '#94a3b8', minWidth: 28 }}>{share.toFixed(0)}%</span>
													</div></td>
												</tr>
											);
										})}
										{!loading && (!headCost || headCost.length === 0) && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>

				{/* ── Project + Category ── */}
				<div className='row g-3'>
					<div className='col-lg-7'>
						<div style={{ borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
							{panelHead('Project Wise Vehicles', '/report-buWiseLightVehicle', 'Report →')}
							<div style={{ maxHeight: 300, overflowY: 'auto' }}>
								<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
									<thead><tr style={tblHeader}>
										<th style={{ paddingLeft: 14 }}>Project</th><th className='text-center'>Active</th><th className='text-center'>Inactive</th>
										<th className='text-center'>In-House</th><th className='text-center'>3rd Party</th><th className='text-center'>Total</th>
									</tr></thead>
									<tbody>
										{(projectVehicle || []).map((r, i) => {
											const act = toNum(r?.totalActiveVehicles);
											const inact = toNum(r?.totalInactiveVehicles);
											const ih = toNum(r?.totalInHouseVehicles);
											const tp = toNum(r?.totalThirdPartyVehicles);
											const tot = toNum(r?.totalVehicle) || act + inact;
											return (
												<tr key={`p-${i}`}>
													<td style={{ paddingLeft: 14 }}>{r?.projectName || r?.project_name || '-'}</td>
													<td className='text-center'>{act}</td>
													<td className='text-center'>{inact}</td>
													<td className='text-center'>{ih || '-'}</td>
													<td className='text-center'>{tp || '-'}</td>
													<td className='text-center fw-bold'>{tot}</td>
												</tr>
											);
										})}
										{!loading && (!projectVehicle || projectVehicle.length === 0) && <tr><td colSpan={6} className='text-muted text-center py-3'>No data</td></tr>}
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<div className='col-lg-5'>
						<div style={{ borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
							{panelHead('Category Wise Vehicles', '/report-typeWiseVehicle', 'Report →')}
							<div style={{ maxHeight: 300, overflowY: 'auto' }}>
								<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
									<thead><tr style={tblHeader}>
										<th style={{ paddingLeft: 14 }}>Category</th><th className='text-center'>Active</th><th className='text-center'>Inactive</th><th className='text-center'>Total</th>
									</tr></thead>
									<tbody>
										{(categoryVehicle || []).map((r, i) => {
											const a = toNum(r?.totalActiveVehicles);
											const ia = toNum(r?.totalInactiveVehicles);
											const t = toNum(r?.totalVehicle) || a + ia;
											return (
												<tr key={`c-${i}`}>
													<td style={{ paddingLeft: 14 }}>{r?.categoryName || r?.category_name || 'Others'}</td>
													<td className='text-center'>{a}</td>
													<td className='text-center'>{ia}</td>
													<td className='text-center fw-bold'>{t}</td>
												</tr>
											);
										})}
										{!loading && (!categoryVehicle || categoryVehicle.length === 0) && <tr><td colSpan={4} className='text-muted text-center py-3'>No data</td></tr>}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ManagementDashboard;
